import { Dispatch, SetStateAction, useEffect } from 'react';
import {
  Modal,
  TextField,
  Button,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import React from 'react';
import { showError, showToast } from '../constants/toasts';
import {
  useAddCategoryMutation,
  useLazyGetCategoryByIdQuery,
  useUpdateCategoryMutation,
} from '../services/shopingMeal';

interface Props {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  editId?: number | string | null;
  isEdit?: boolean;
  onSuccess?: () => void;
}

export default function AddShoppingCategory({ open, onClose, setOpen, editId = null, isEdit = false, onSuccess }: Props) {
  const [categoryName, setCategoryName] = React.useState<string>('');
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [addCategory] = useAddCategoryMutation();
  const [updateCategory] = useUpdateCategoryMutation();
  const [getCategoryById] = useLazyGetCategoryByIdQuery();

  useEffect(() => {
    const init = async () => {
      if (open && isEdit && editId) {
        try {
          const idNum = typeof editId === 'string' ? parseInt(editId, 10) : editId;
          if (!idNum || Number.isNaN(idNum)) return;
          const response = await getCategoryById({ id: idNum }).unwrap();
          const name = response?.data?.name || response?.data?.category_name || '';
          setCategoryName(name);
        } catch (e: any) {
          showError(e?.data?.message || 'Failed to load category');
        }
      } else if (open && !isEdit) {
        setCategoryName('');
      }
    };
    init();
  }, [open, isEdit, editId, getCategoryById]);

  const handleSubmit = async (name: string) => {
    setError(true);
    const trimmedName = name?.trim() ?? '';
    if (!trimmedName.length) {
      showError('Please input catgory name');
      return;
    }
    setIsLoading(true);
    try {
      let response: any;
      if (isEdit) {
        const idNum = isEdit && editId ? (typeof editId === 'string' ? parseInt(editId, 10) : editId) : undefined;
        response = await updateCategory({ body: { name: trimmedName }, id: idNum as number }).unwrap();
      } else {
        response = await addCategory({ body: { name: trimmedName } }).unwrap();
      }
      if (response?.status === 200 || response?.status === 201 || response?.statusCode === 200) {
        setCategoryName('');
        setOpen(false);
        onSuccess?.();
        showToast(response?.message || (isEdit ? 'Category updated successfully' : 'Category added successfully'));
      }
    } catch (error: any) {
      showError(error?.data?.message || 'Something went wrong');
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Modal
      className="modal alertConfig_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog">
        <div className="modal-body">
          <div className="modal_title">
            <h2>{isEdit ? 'Edit Shopping Category' : 'Add Shopping Category'}</h2>
            <div className="btn-close" onClick={() => setOpen(false)}>
              <CloseIcon />
            </div>
          </div>

          <form className="form" action="">
            
            <div className="control_group">
              <label> Category Name</label>
              <TextField
                fullWidth
                hiddenLabel
                placeholder="Enter"
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
              />
              {error && !categoryName.trim() ? (
                <h6 className="err_msg">Category name is required</h6>
              ) : (
                ''
              )}
            </div>
            <div className="footer_btn_flex">
              <Button
                className="btn btn_grey"
                onClick={() => {
                  setError(false);
                  setOpen(false);
                  setCategoryName('');
                }}
                disabled={isLoading}
              >Cancel</Button>
              <Button
                className="btn btn_primary"
                onClick={() => handleSubmit(categoryName)}
                disabled={isLoading}
              >{isLoading ? 'Processing...' : isEdit ? 'Update' : 'Submit'}</Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
